// src/pages/po/HistoricoItemModalContent.tsx
import React, { useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClockRotateLeft, faRectangleXmark } from '@/icons';
import ScrollableTable from '../../components/common/ScrollableTable';
import { apiGet } from '../../services/api';
import { HistoricoResponse, HistoricoItem } from '../../types/compras/Historico';
import { formatDateTime } from '../../utils/formatUtils';
import './HistoricoItemModalContent.css';
import { logger } from '@/utils/logger';

interface HistoricoItemModalContentProps {
  historico: HistoricoResponse;
  requisicaoItemId: number;
  isOpen: boolean;
  onClose: () => void;
  theme: 'dark' | 'light';
  showCancelButton?: boolean;
}

const HistoricoItemModalContent: React.FC<HistoricoItemModalContentProps> = ({
  historico,
  requisicaoItemId,
  isOpen,
  onClose,
  theme,
  showCancelButton = true,
}) => {
  // Log da resposta completa da API
  logger.log('Resposta completa da API (historico):', historico);

  const [items, setItems] = useState<HistoricoItem[]>(historico.historicos.content);
  const [hasMore, setHasMore] = useState(historico.historicos.page.number < historico.historicos.page.totalPages - 1);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(historico.historicos.page.number);

  // Log inicial ajustado
  logger.log('Dados ajustados:', {
    number: historico.historicos.page.number,
    totalPages: historico.historicos.page.totalPages,
    totalElements: historico.historicos.page.totalElements,
    contentLength: historico.historicos.content.length,
    hasMoreInitial: historico.historicos.page.number < historico.historicos.page.totalPages - 1,
  });

  // Dados do cabeçalho
  const { codProduto, codCategoriaProduto, nomeProduto, nomeCategoria } = historico;

  // Função para carregar mais itens
  const loadMore = useCallback(async () => {
  if (isLoading || !hasMore) {
    logger.log('loadMore não chamado - isLoading:', isLoading, 'hasMore:', hasMore);
    return;
  }
  setIsLoading(true);
  logger.log(`Carregando mais itens - Página atual: ${currentPage}, Próxima página: ${currentPage + 1}`);
  try {
    const nextPage = currentPage + 1;
    // Adiciona parâmetros obrigatórios codProduto e codCategoriaProduto, e itemModuloId como opcional
    const queryParams = new URLSearchParams({
      codProduto: historico.codProduto,
      codCategoriaProduto: historico.codCategoriaProduto.toString(),
      itemModuloId: requisicaoItemId.toString(),
      page: nextPage.toString(),
      size: '10',
    });
    const res = await apiGet(`/v1/historico-itens?${queryParams.toString()}`);
    logger.log('Resposta completa da API (loadMore):', res.data);
    const newData = res.data.historicos.content;
    const totalPages = res.data.historicos.page.totalPages;
    const totalElements = res.data.historicos.page.totalElements;
    logger.log('Itens carregados:', newData);
    logger.log('Total de páginas:', totalPages);
    logger.log('Total de elementos:', totalElements);
    setItems((prev) => [...prev, ...newData]);
    const newHasMore = nextPage < totalPages - 1;
    setHasMore(newHasMore);
    logger.log('Novo valor de hasMore:', newHasMore);
    setCurrentPage(nextPage);
  } catch (err) {
    logger.error('Erro ao carregar mais históricos:', err);
  } finally {
    setIsLoading(false);
  }
}, [isLoading, hasMore, currentPage, requisicaoItemId, historico.codProduto, historico.codCategoriaProduto]);

  if (!isOpen) return null;

  // Definição das colunas da tabela
  const columns = [
    {
      header: 'Criação',
      render: (entry: HistoricoItem) => formatDateTime(entry.dataHora),
    },
    {
      header: 'Descrição',
      render: (entry: HistoricoItem) => entry.descricao,
    },
    {
      header: 'Usuário',
      render: (entry: HistoricoItem) => entry.nome,
    },
    {
      header: 'Filial',
      render: (entry: HistoricoItem) => entry.nomeFantasia,
    },
  ];

  return (
    <div className={`historico-item-modal-content ${theme}`}>
      <div className="modal-header">
        <h4>
          <FontAwesomeIcon icon={faClockRotateLeft} style={{ marginRight: '8px' }} /> Histórico do Item
        </h4>
      </div>
      <div className="item-info">
        <p>
          Código: {codProduto || ''} | Cód. Categoria: {codCategoriaProduto} | Nome do Produto: {nomeProduto || 'Erro de Integração'} | Categoria: {nomeCategoria || 'Erro de Integração'}
        </p>
      </div>
      <ScrollableTable<HistoricoItem>
        data={items}
        hasMore={hasMore}
        loadMore={loadMore}
        isLoading={isLoading}
        columns={columns}
        onSelectChange={() => {}}
        showCheckboxes={false}
        isModal={true}
        emptyMessage="Nenhum registro de histórico encontrado."
      />
      {showCancelButton && (
        <div className="form-actions">
          <button onClick={onClose} className="cancel-button">
            <FontAwesomeIcon icon={faRectangleXmark} /> Cancelar
          </button>
        </div>
      )}
    </div>
  );
};

export default HistoricoItemModalContent;